import React from 'react';
import PropTypes from 'prop-types';
import Switch from '@material-ui/core/Switch';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './SearchFieldSetting.module.scss';

const PRIORITIES = [
  { value: 'IGNORE', labelKey: 'search.fields.priority.ignore' },
  { value: 'NORMAL', labelKey: 'search.fields.priority.normal' },
  { value: 'MID', labelKey: 'search.fields.priority.mid' },
  { value: 'BOOSTED', labelKey: 'search.fields.priority.boosted' },
];

const SearchFieldSetting = ({
  id,
  value,
  title,
  description,
  onChangeWeight,
  onChangeAutocomplete,
  autocomplete,
  hideAutocomplete,
}) => {
  const localizeMessage = useLocalizedMessage();
  const pointerStartRef = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragPosition, setDragPosition] = React.useState(null);
  const activePriorityIndex = Math.max(0, PRIORITIES.findIndex((priority) => priority.value === value));
  const activePriority = PRIORITIES[activePriorityIndex];
  const position = `${(activePriorityIndex / (PRIORITIES.length - 1)) * 100}%`;
  const displayPosition = isDragging && dragPosition !== null ? `${dragPosition}%` : position;

  const changePriority = (event) => {
    const nextPriority = PRIORITIES[Number(event.target.value)];
    if (nextPriority && nextPriority.value !== value) {
      onChangeWeight(id, nextPriority.value);
    }
  };

  const startPointerInteraction = (event) => {
    pointerStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
    };

    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const trackPointerInteraction = (event) => {
    const pointerStart = pointerStartRef.current;
    if (!pointerStart || pointerStart.pointerId !== event.pointerId) {
      return;
    }

    if (Math.abs(event.clientX - pointerStart.x) >= 4) {
      setIsDragging(true);

      const bounds = event.currentTarget.getBoundingClientRect();
      const railInset = 10;
      const railWidth = bounds.width - railInset * 2;
      if (railWidth > 0) {
        const relativeX = event.clientX - bounds.left - railInset;
        const nextPosition = Math.min(100, Math.max(0, (relativeX / railWidth) * 100));
        setDragPosition(nextPosition);
      }
    }
  };

  const finishPointerInteraction = (event) => {
    pointerStartRef.current = null;
    setIsDragging(false);
    setDragPosition(null);

    if (
      event.currentTarget.releasePointerCapture
      && event.currentTarget.hasPointerCapture
      && event.pointerId !== undefined
      && event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const cancelPointerInteraction = () => {
    pointerStartRef.current = null;
    setIsDragging(false);
    setDragPosition(null);
  };

  return (
    <div
      className={`${styles.row} ${hideAutocomplete ? '' : styles.withAutocomplete}`.trim()}
      data-testid="search-field-setting"
    >
      <div className={styles.identity}>
        <span className={styles.title}>{title}</span>
        {description && <span className={styles.description}>{description}</span>}
      </div>

      <div
        className={styles.priorityControl}
        style={{ '--priority-position': displayPosition }}
        data-priority-value={activePriority.value}
        data-dragging={isDragging ? 'true' : undefined}
        data-drag-position={isDragging && dragPosition !== null ? Math.round(dragPosition) : undefined}
        data-testid="search-field-priority"
      >
        <div className={styles.priorityScale}>
          <input
            className={styles.priorityInput}
            type="range"
            min="0"
            max={PRIORITIES.length - 1}
            step="1"
            value={activePriorityIndex}
            aria-label={localizeMessage('search.fields.priority.ariaLabel', { field: title })}
            aria-valuetext={localizeMessage(activePriority.labelKey)}
            onChange={changePriority}
            onPointerDown={startPointerInteraction}
            onPointerMove={trackPointerInteraction}
            onPointerUp={finishPointerInteraction}
            onPointerCancel={finishPointerInteraction}
            onBlur={cancelPointerInteraction}
          />
          <div className={styles.priorityRail} aria-hidden="true">
            <span className={styles.priorityFill} />
            <span className={styles.priorityThumb} />
          </div>
        </div>
        <div className={styles.priorityLabels} aria-hidden="true">
          {PRIORITIES.map((priority, index) => {
            const selected = value === priority.value;
            return (
              <span
                key={priority.value}
                className={`${styles.priorityLabel} ${selected ? styles.priorityLabelActive : ''}`.trim()}
                style={{ left: `${(index / (PRIORITIES.length - 1)) * 100}%` }}
              >
                {localizeMessage(priority.labelKey)}
              </span>
            );
          })}
        </div>
      </div>

      {!hideAutocomplete && (
        <div className={styles.autocompleteCell}>
          <Switch
            color="primary"
            checked={autocomplete}
            onChange={(event) => onChangeAutocomplete && onChangeAutocomplete(id, event.target.checked)}
            inputProps={{
              'aria-label': localizeMessage('search.fields.autocomplete.ariaLabel', { field: title }),
            }}
          />
        </div>
      )}
    </div>
  );
};

SearchFieldSetting.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.oneOf(['IGNORE', 'NORMAL', 'MID', 'BOOSTED']).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  onChangeWeight: PropTypes.func.isRequired,
  onChangeAutocomplete: PropTypes.func,
  autocomplete: PropTypes.bool,
  hideAutocomplete: PropTypes.bool,
};

SearchFieldSetting.defaultProps = {
  description: null,
  onChangeAutocomplete: null,
  autocomplete: false,
  hideAutocomplete: false,
};

export default SearchFieldSetting;
